import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FirebaseOptions, initializeApp } from 'firebase/app'
import { isSupported, getMessaging, getToken } from 'firebase/messaging'
import Config from 'config'
import { getLoggedInMemberId } from 'redux/selectors/members'
import { registerForPushNotifications } from 'redux/thunks/api'
import { APP_NAME } from 'util/constants'
import { toast } from 'util/toast'

const firebaseCoreConfig = {
  apiKey: Config.firebaseApiKey,
  authDomain: Config.firebaseAuthDomain,
  projectId: Config.firebaseProjectId,
  storageBucket: Config.firebaseStorageBucket,
  appId: Config.firebaseAppId,
} as const satisfies FirebaseOptions

const firebaseConfig = {
  ...firebaseCoreConfig,
  databaseURL: 'https://timely-member.firebaseio.com',
} as const satisfies FirebaseOptions

const firebaseConfigWithMessaging = {
  ...firebaseCoreConfig,
  messagingSenderId: Config.fireBaseMessagingSenderId,
  databaseURL: 'https://timely-member.firebaseio.com',
} as const satisfies FirebaseOptions

const isNotificationSupported = 'Notification' in window

export const app = isNotificationSupported ? initializeApp(firebaseConfigWithMessaging) : initializeApp(firebaseConfig)

// api is expecting mobile specific values, so setting a generic device for web
const device = {
  make: 'web',
  model: 'web',
  modelVersion: 'web',
  platform: 'web',
  appVersion: 'web',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

const PushService = () => {
  const dispatch = useDispatch()
  const memberId = useSelector(getLoggedInMemberId)
  const [hasNotifPerm, setHasNotifPerm] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)

  const requestPermission = useCallback(async () => {
    const permission = await window.Notification?.requestPermission?.()
    setHasNotifPerm(permission === 'granted')
  }, [])

  const checkPermission = useCallback(async () => {
    if (window.Notification?.permission === 'granted') {
      setHasNotifPerm(true)
    } else {
      toast.success(
        <p className="typography-body">
          {APP_NAME} would like to send you push notifications. Click{' '}
          <button
            className="btn-unsized text-white underline"
            aria-label="Allow Notifications"
            onClick={requestPermission}
            type="button"
          >
            here
          </button>{' '}
          to grant access.
        </p>,
        {
          hideProgressBar: true,
          autoClose: false,
          role: 'alert',
        },
      )
    }
  }, [requestPermission])

  const identifyServiceWorker = async () => {
    try {
      const sw = await navigator.serviceWorker?.getRegistration?.()
      if (sw) {
        sw.update()
        setIsRegistered(true)
      } else {
        setIsRegistered(false)
      }
    } catch (e) {
      setIsRegistered(false)
    }
  }

  const requestToken = useCallback(async () => {
    try {
      if (await isSupported()) {
        const messaging = getMessaging(app)
        const token = await getToken(messaging, { vapidKey: Config.firebaseVapidKey })
        if (token) {
          dispatch(registerForPushNotifications(token, memberId, device))
        }
      }
    } catch (e) {
      console.log('An error occurred while retrieving push token. ', e)
    }
  }, [dispatch, memberId, device])

  useEffect(() => {
    if (memberId) {
      identifyServiceWorker()
    }
  }, [memberId])

  useEffect(() => {
    if (isNotificationSupported) {
      if (memberId) {
        if (!hasNotifPerm) {
          checkPermission()
        } else if (!isRegistered) {
          requestToken()
        }
      }
    }
  }, [memberId, requestToken, hasNotifPerm, isRegistered, checkPermission])

  return null
}

export default PushService
